import { useState, useEffect, useRef } from "react";
import BaseModal from "./BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import Icon1 from "@assets/icons/Icon";
import {
  serviceboxProxy,
  PROXY_BUSY_MESSAGE,
  type AgendaOption,
  type CreateRdvParams,
  type StepResult,
  type LogEntry,
  type TestConnectionResult,
} from "@services/serviceboxProxy";
import { showNotification } from "context/notification.service";
import { ordresApi } from "@services/api/ordres";
import { interventionsApi } from "@services/api/interventions";
import { appointmentsApi, type ReceptionConflictResult, type ReceptionConflictEvent } from "@services/api/appointments";
import { usePlanningStore } from "@store/planningStore";
import { dossiersApi } from "@services/api/dossiers";
import { useOrdreStore } from "@store/ordreStore";
import type { Ordre } from "types/ordre";
import { isAPrevoir } from "types/ordre";
import type { Dossier, DossierType } from "types/dossier";
import type { OrgServiceBox, OrgServiceBoxSite } from "types/org";
import { useDataStore } from "@store/user_org";
import { isTimeOffGrid } from "@utils/timeGrid";
import { isChromiumBrowser } from "@utils/browser";
import { buildCreateRdvParams, withVinFromPlate, withAlphaNumber, firstLinkedReceptionnaireId, humanizeServiceboxError, isOnServiceBox, serviceboxRdvId, vehicleMarque } from "@utils/serviceboxTransfer";
import { serviceboxSyncApi } from "@services/api/serviceboxSync";
import { alphaPosteFor, startAlphaTransfer } from "@services/serviceboxAlphaTransfer";

/** Map common marque strings to a normalised key for matching against sites */
function normaliseMarque(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function detectSite(
  sites: OrgServiceBoxSite[] | undefined,
  vehicleMarque: string | undefined,
): OrgServiceBoxSite | undefined {
  if (!sites?.length || !vehicleMarque) return undefined;
  const key = normaliseMarque(vehicleMarque);
  return sites.find((s) => normaliseMarque(s.marque) === key);
}

interface Props {
  ordre: Ordre;
  serviceboxConfig: OrgServiceBox;
  onClose: () => void;
  /** Called after a successful transfer with the fields persisted on the ordre
   *  (or_number, num, servicebox_rdv_id, servicebox_dossier_id) so the host can
   *  refresh a non-store-subscribed copy of the ordre (e.g. the planning OrdreModal). */
  onTransferred?: (updates: Partial<Ordre>) => void;
  /** Auto-run mode: skip the form and immediately run the (update) transfer with a minimal
   *  loading view. Used to push a client-info edit onto an already-transferred RDV. */
  autoRun?: boolean;
}

/** Normalise an intervention label/name for matching ordre interventions against the catalog. */
function normaliseLabel(raw: string): string {
  return raw.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/** Formats attendus par l'API Carvis (`YYYY-MM-DD` / `HH:MM`) — a ne pas confondre
 *  avec les formats compacts ci-dessus, qui sont ceux du payload ServiceBox. Envoyer
 *  "20260924"/"1000" a /appointments/check-reception-conflicts faisait repondre
 *  {reception: null} : plus de detection de creneau pris, et surtout plus de colonne
 *  de reception pour proposer le bon receptionnaire par defaut. */
function formatDateISO(ts?: { seconds: number }): string {
  if (!ts) return "";
  const d = new Date(ts.seconds * 1000);
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}

function formatTimeColon(ts?: { seconds: number }): string {
  if (!ts) return "";
  const d = new Date(ts.seconds * 1000);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

/** "07:38" for display. */
function formatTimeDisplay(ts?: { seconds: number }): string {
  if (!ts) return "—";
  const d = new Date(ts.seconds * 1000);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

const STEP_ICON: Record<string, { color: string; icon: string }> = {
  ok: { color: "#059669", icon: "done2" },
  error: { color: "#DC2626", icon: "close" },
  skipped: { color: "#9CA3AF", icon: "minus" },
};

function StepLine({ step }: { step: StepResult }) {
  const style = STEP_ICON[step.status] || STEP_ICON.skipped;
  return (
    <div className="flex flex-row items-center gap-2 py-1">
      <Icon1 name={style.icon} size={14} color={style.color} />
      <span className="text-h3 font-medium" style={{ color: style.color }}>{step.name}</span>
      {step.detail && (
        <span className="text-h4 text-neutral-400 ml-auto">{step.detail}</span>
      )}
    </div>
  );
}

const DOSSIER_TYPE_LABELS: Record<string, string> = {
  particulier: "Particulier",
  professionnel: "Professionnel",
  loueur: "Loueur",
  garantie: "Garantie",
  occasion: "Véhicule occasion",
  interne: "Véhicule interne",
};

export default function ServiceBoxTransferModal({ ordre, serviceboxConfig, onClose, onTransferred, autoRun }: Props) {
  const [receptionnaires, setReceptionnaires] = useState<AgendaOption[]>([]);
  const [equipes, setEquipes] = useState<AgendaOption[]>([]);
  const [selectedReceptionnaire, setSelectedReceptionnaire] = useState("");
  // ServiceBox allows a different person for the restitution. Defaults to the
  // reception receptionnaire (set when options load), editable independently.
  const [selectedRestitutionReceptionnaire, setSelectedRestitutionReceptionnaire] = useState("");
  // Choix manuel de l'équipe ; vide = suit le réceptionnaire (voir `selectedEquipe`).
  const [pickedEquipe, setPickedEquipe] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [waitingForLock, setWaitingForLock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepResult[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connectionTest, setConnectionTest] = useState<TestConnectionResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [debugResults, setDebugResults] = useState<Array<{ test: string; result: string; detail: string }> | null>(null);
  const [debugging, setDebugging] = useState(false);
  const [searchingClient, setSearchingClient] = useState(false);
  const [clientSearchResult, setClientSearchResult] = useState<{ found: boolean; detail: string } | null>(null);
  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);
  const [receptionConflict, setReceptionConflict] = useState<ReceptionConflictResult | null>(null);
  // ServiceBox internal dossier id known after a successful transfer (or already on the ordre),
  // used to build the "Voir le Dossier ServiceBox" link in the success state.
  const [dossierId, setDossierId] = useState<string | null>(ordre.servicebox_dossier_id || null);
  // Catalog forfait codes keyed by normalised intervention name — fallback for ordre
  // interventions created before the forfait_code was denormalised onto them.
  const [forfaitByLabel, setForfaitByLabel] = useState<Record<string, string>>({});
  const [fetchingOr, setFetchingOr] = useState(false);
  const [orNumber, setOrNumber] = useState(ordre.or_number || "");

  useEffect(() => {
    if (ordre.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre.dossier_id]);

  // Predict a ServiceBox "créneau refusé": is the reception/restitution slot on a
  // reception column already taken by another RDV?
  const refreshConflicts = () => {
    if (!ordre.id) return;
    appointmentsApi
      .checkReceptionConflicts({
        ordre_id: ordre.id,
        reception_date: formatDateISO(ordre.reception_date) || undefined,
        reception_time: formatTimeColon(ordre.reception_date) || undefined,
        restitution_date: formatDateISO(ordre.restitution) || undefined,
        restitution_time: formatTimeColon(ordre.restitution) || undefined,
      })
      .then(setReceptionConflict)
      .catch(() => {});
  };
  useEffect(refreshConflicts, [ordre.id]);

  const switchReception = async (ev: ReceptionConflictEvent, recId: string) => {
    const free = ev.free_receptions.find((r) => r.id === recId);
    if (!free || !ev.appointment_id) return;
    try {
      await appointmentsApi.update(ev.appointment_id, { category: free.id });
      showNotification({ type: "success", message: `Basculé sur ${free.label}` });
      const ps = usePlanningStore.getState();
      if (ps._fetchedOrgId) ps.fetchAppointments(ps._fetchedOrgId, true);
      refreshConflicts();
    } catch {
      showNotification({ type: "error", message: "Échec du changement de réception" });
    }
  };

  const dossierType = parentDossier?.type as DossierType | undefined;

  const sites = serviceboxConfig.sites || [];
  const autoSite = detectSite(sites, vehicleMarque(ordre.vehicle));
  // When the vehicle's brand doesn't match a site (e.g. a Fiat), default to the Peugeot
  // point de vente rather than the org's configured site_code (which is a Citroën site).
  const peugeotSite = sites.find(
    (s) => normaliseMarque(s.marque).includes("PEUG") || normaliseMarque(s.label).includes("PEUG"),
  );
  const defaultPdv = autoSite?.pdv_id || peugeotSite?.pdv_id || serviceboxConfig.site_code || "";
  const [selectedPdv, setSelectedPdv] = useState(defaultPdv);
  // Le véhicule peut arriver après l'ouverture : sans ce recalage le point de vente
  // restait celui de Peugeot pour une Citroën (dossier SB dans le mauvais groupe de
  // marque → campagnes de rappel invisibles). Un choix manuel n'est jamais écrasé.
  const userTouchedPdvRef = useRef(false);
  useEffect(() => {
    if (!userTouchedPdvRef.current && defaultPdv) setSelectedPdv(defaultPdv);
  }, [defaultPdv]);

  const activeSiteCode = selectedPdv || serviceboxConfig.site_code;
  const operatorEmail = useDataStore((s) => s.userData?.infos?.email) || "";
  const orgId = useDataStore((s) => s.orgData?.id);
  const orgReceptions = useDataStore((s) => s.orgData?.receptions);
  // Once the user picks a réceptionnaire manually, stop auto-defaulting it from the réception link.
  const userTouchedRecepRef = useRef(false);

  // Load the catalog once to resolve forfait codes by intervention name.
  useEffect(() => {
    if (!orgId) return;
    interventionsApi
      .list(orgId)
      .then((list) => {
        const map: Record<string, string> = {};
        list.forEach((it) => {
          if (it.forfait_code) map[normaliseLabel(it.name)] = it.forfait_code;
        });
        setForfaitByLabel(map);
      })
      .catch(() => {});
  }, [orgId]);

  const creds = {
    username: serviceboxConfig.username || "",
    password: serviceboxConfig.password || "",
    site_code: activeSiteCode,
    operator_email: operatorEmail,
  };

  const primaryContact = ordre.contacts?.find((c) => c.is_primary) || ordre.contacts?.[0];
  const vehicle = ordre.vehicle;
  const kmEmpty = !((vehicle?.km != null && String(vehicle.km).trim() !== "") || ordre.booking_request?.km);

  // ─── Pre-transfer validation: block when essential info is missing ───
  const missingFields: string[] = [];
  if (!dossierType) missingFields.push("Type de dossier");
  if (!(vehicle?.modele || ordre.modele)) missingFields.push("Modèle du véhicule");
  if (!(ordre.immatriculation || vehicle?.plate)) missingFields.push("Immatriculation");
  // Block transfer when there's no intervention — otherwise ServiceBox would
  // receive an empty OR.
  // Only "inclus" interventions are billed on this RDV. "À prévoir" lines are saved to the
  // dossier (ServiceBox Travaux à prévoir — Phase 2) and must not create work lines here.
  if (!ordre.interventions || ordre.interventions.filter((i) => !isAPrevoir(i)).length === 0) missingFields.push("Au moins une intervention incluse");
  if (!ordre.reception_date?.seconds) missingFields.push("Date de réception");
  if (!ordre.restitution?.seconds) missingFields.push("Date de restitution");
  const datesOutOfOrder =
    !!ordre.reception_date?.seconds &&
    !!ordre.restitution?.seconds &&
    ordre.restitution.seconds < ordre.reception_date.seconds;
  // The RDV is already on ServiceBox, but under the id an IMPORT writes
  // (`sb_agenda_rdv_id`), which `buildCreateRdvParams` does not send as `rdv_id` — so the
  // proxy would run its CREATE path and ask ServiceBox for a second RDV on top of its own.
  // ServiceBox answers "CCS indisponible" while the slot is still its own, and creates a
  // duplicate once the slot has moved in Carvis. Neither is ever wanted.
  //
  // Sending `sb_agenda_rdv_id` as `rdv_id` is NOT the fix: the proxy's update path rebuilds
  // the form from the request and, as `heric-back/app/services/sb_forms.py` puts it, "wipes
  // the RDV's work lines, flags and comments" — on an imported RDV that means its numéro
  // d'OR, its état d'avancement and the réceptionnaire's commentaire. Reporting an
  // intervention onto a RDV that already exists goes through the interventions push.
  const wouldDuplicateOnServiceBox = isOnServiceBox(ordre) && !ordre.servicebox_rdv_id;
  const canTransfer = missingFields.length === 0 && !datesOutOfOrder && !wouldDuplicateOnServiceBox;

  // ServiceBox refuses times that fall outside its programmed slots (off the
  // 7.5-min grid, before the first / after the last slot). Off-grid times are the
  // usual cause of "CCS indisponible" — warn proactively (non-blocking).
  const offGridTimes: string[] = [];
  if (isTimeOffGrid(ordre.reception_date?.seconds)) offGridTimes.push(`réception ${formatTimeDisplay(ordre.reception_date)}`);
  if (isTimeOffGrid(ordre.restitution?.seconds)) offGridTimes.push(`restitution ${formatTimeDisplay(ordre.restitution)}`);

  useEffect(() => {
    serviceboxProxy
      .getOptions(creds)
      .then((opts) => {
        setReceptionnaires(opts.receptionnaires);
        setEquipes(opts.equipes);
        if (opts.receptionnaires.length > 0) {
          // Repli = la premiere reception de l'org reellement liee a une colonne
          // ServiceBox. `receptionnaires[0]` pouvait tomber sur une colonne non
          // reservable (TAKATA, carrosserie) et faire echouer le transfert en
          // « CCS indisponible ». Ecrase juste apres par la colonne de l'ordre
          // quand sa reception est mappee (effet ci-dessous).
          const fallback = firstLinkedReceptionnaireId(orgReceptions, opts.receptionnaires);
          setSelectedReceptionnaire(fallback);
          setSelectedRestitutionReceptionnaire(fallback);
        }
      })
      .catch((e) => setError("Impossible de contacter le proxy ServiceBox: " + e.message))
      .finally(() => setLoading(false));
  }, []);

  // Default the réceptionnaire from the ordre's réception → ServiceBox link (Paramètres →
  // Réceptions), overriding the first-option fallback, unless the user changed it manually.
  useEffect(() => {
    if (userTouchedRecepRef.current || receptionnaires.length === 0) return;
    const linkFor = (cat?: string) => {
      if (!cat) return "";
      const rec = (orgReceptions || []).find((r) => r.id === cat);
      const sid = rec?.servicebox_receptionnaire_id;
      return sid && receptionnaires.some((r) => r.id === sid) ? sid : "";
    };
    const recLink = linkFor(receptionConflict?.reception?.category);
    const restLink = linkFor(receptionConflict?.restitution?.category) || recLink;
    if (recLink) setSelectedReceptionnaire(recLink);
    if (restLink) setSelectedRestitutionReceptionnaire(restLink);
  }, [receptionConflict, orgReceptions, receptionnaires]);

  // Le select affiche le nom ServiceBox de la colonne (« RECEPTION 2 »), qui ne dit
  // rien a qui raisonne en colonnes de planning Carvis (« LANA »). On accole les deux.
  const receptionnaireLabel = (r: AgendaOption) => {
    const rec = (orgReceptions || []).find((o) => o.servicebox_receptionnaire_id === r.id);
    return rec ? `${r.name} — ${rec.label}` : r.name;
  };

  // Un RDV pris au guichet carrosserie part en équipe CARROSSERIE, les autres en
  // MECANIQUE — sauf choix manuel. Dérivé (pas un effet) pour que l'autoRun parte
  // directement avec la bonne équipe.
  const isCarrosserieDesk = (() => {
    const r = receptionnaires.find((o) => o.id === selectedReceptionnaire);
    const rec = (orgReceptions || []).find((o) => o.servicebox_receptionnaire_id === selectedReceptionnaire);
    return /CARROSS/i.test(`${r?.name || ""} ${rec?.id || ""} ${rec?.label || ""}`);
  })();
  const autoEquipe =
    (isCarrosserieDesk && equipes.find((e) => e.name.toUpperCase().includes("CARROSS"))) ||
    equipes.find((e) => e.name.toUpperCase().includes("MECANIQUE")) ||
    equipes[0];
  const selectedEquipe = pickedEquipe || autoEquipe?.id || "";

  // D'ou vient la colonne proposee : de la colonne de reception de l'ordre quand elle
  // est liee, d'un repli sinon — et dans ce cas il faut le dire, c'est la que le
  // transfert part dans la mauvaise colonne.
  const recCategory = receptionConflict?.reception?.category;
  const recOrgReception = (orgReceptions || []).find((r) => r.id === recCategory);
  const recIsLinked = !!(
    recOrgReception?.servicebox_receptionnaire_id &&
    receptionnaires.some((r) => r.id === recOrgReception.servicebox_receptionnaire_id)
  );
  const receptionnaireHint = !recOrgReception
    ? null
    : recIsLinked
      ? `Repris de la colonne du planning : ${recOrgReception.label}.`
      : `La colonne « ${recOrgReception.label} » n'est liée à aucune colonne ServiceBox (Paramètres → Réceptions) : la colonne ci-dessus est un repli, vérifiez-la.`;

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionTest(null);
    try {
      const result = await serviceboxProxy.testConnection(creds);
      setConnectionTest(result);
    } catch (e: any) {
      setConnectionTest({
        connected: false,
        session_ok: false,
        servicebox_reachable: false,
        detail: e.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDebugAuth = async () => {
    setDebugging(true);
    setDebugResults(null);
    try {
      const data = await serviceboxProxy.debugAuth(creds);
      setDebugResults(data.results);
    } catch (e: any) {
      setDebugResults([{ test: "Error", result: "error", detail: e.message }]);
    } finally {
      setDebugging(false);
    }
  };

  const handleSearchClient = async () => {
    setSearchingClient(true);
    setClientSearchResult(null);
    try {
      const phone = (primaryContact?.phone || "").replace(/\s/g, "");
      const nom = primaryContact?.nom || ordre.name || "";
      const result = await serviceboxProxy.searchClient(creds, phone, nom);
      setClientSearchResult(result);
    } catch (e: any) {
      setClientSearchResult({ found: false, detail: e.message });
    } finally {
      setSearchingClient(false);
    }
  };

  const handleFetchLogs = async () => {
    try {
      const entries = await serviceboxProxy.getLogs(30);
      setLogs(entries);
      setShowLogs(true);
    } catch {
      setLogs([]);
      setShowLogs(true);
    }
  };

  // Carvis never creates the OR — Alpha prints the priced CT the moment it does, so that
  // transfer is the garage's own move, made from ServiceBox when the car is there. All we
  // ever do is read back the number Alpha wrote on the RDV.
  const fetchOrNumber = async () => {
    setFetchingOr(true);
    setError(null);
    try {
      const res = await serviceboxSyncApi.refreshOrNumber(ordre.id);
      if (!res.success) {
        setError(res.error || "Lecture du numéro d'OR impossible");
        return;
      }
      if (res.or_number) {
        setOrNumber(res.or_number);
        onTransferred?.({ or_number: res.or_number, num: `OR-${res.or_number}` } as Partial<Ordre>);
        showNotification({ type: "success", message: `Numéro d'OR récupéré : OR-${res.or_number}` });
      } else {
        setError(res.detail || "ServiceBox n'affiche pas encore de numéro d'OR sur ce rendez-vous.");
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Erreur");
    } finally {
      setFetchingOr(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedReceptionnaire || !selectedEquipe) {
      setError("Selectionnez un receptionnaire et une equipe");
      return;
    }
    if (!canTransfer) {
      setError(
        wouldDuplicateOnServiceBox
          ? "Ce rendez-vous existe déjà sur ServiceBox : le transférer en créerait un second. Passez par « Mettre à jour ServiceBox » (menu Actions de l'ordre) pour y reporter une intervention ou récupérer le numéro d'OR."
          : datesOutOfOrder
            ? "La date de réception doit être avant la date de restitution."
            : `Informations manquantes pour le transfert : ${missingFields.join(", ")}.`,
      );
      return;
    }
    setSending(true);
    setError(null);
    setSteps([]);

    try {
      // Transfer is gated on having at least one intervention (see missingFields), so the
      // shared builder never produces an empty OR. Payload construction (travaux, client
      // from the canonical dossier contact, address, update-vs-create) lives in
      // buildCreateRdvParams so this flow and the batch pre-transfer screen stay identical.
      const params: CreateRdvParams = await withAlphaNumber(await withVinFromPlate(buildCreateRdvParams({
        ordre,
        parentDossier,
        creds,
        receptionnaireId: selectedReceptionnaire,
        restitutionReceptionnaireId: selectedRestitutionReceptionnaire,
        equipeId: selectedEquipe,
        pdvId: activeSiteCode || "011622H",
        forfaitByLabel,
      }), ordre.org_id), ordre.org_id);

      // ServiceBox half only (~3 s). The Alpha push (~15-25 s, the garage DMS creating the
      // OR) follows in the background so the user is released as soon as the RDV exists.
      const result = await serviceboxProxy.createRdvServiceBoxOnly(params, () => setWaitingForLock(true));
      setWaitingForLock(false);
      // A proxy older than 3.36.0 ignores skip_alpha and runs the whole transfer itself.
      const alphaAlreadyRan = (result.steps || []).some((s) => s.name === "Reponse DMS");
      // Drop the "Transfert Alpha: skipped" placeholder — the background job reports that half.
      setSteps((result.steps || []).filter((s) => alphaAlreadyRan || s.name !== "Transfert Alpha"));

      if (!result.success) {
        setError(humanizeServiceboxError(result.error, {
          reception: formatTimeDisplay(ordre.reception_date),
          restitution: formatTimeDisplay(ordre.restitution),
          alreadyOnServiceBox: isOnServiceBox(ordre),
        }));
        setSending(false);
        return;
      }

      // Save OR number + rdv_id for potential revert
      // NOTE: result.dossier_id is the ServiceBox internal dossier ID, NOT the Firestore dossier_id.
      // Do NOT overwrite ordre.dossier_id with it — that breaks the link to contacts and vehicle.
      const updatePayload: Record<string, any> = {};
      if (result.or_number) updatePayload.or_number = result.or_number;
      if (result.or_number) updatePayload.num = `OR-${result.or_number}`;
      if (result.or_number) setOrNumber(result.or_number);
      if (result.rdv_id) updatePayload.servicebox_rdv_id = result.rdv_id;
      // Client inconnu d'Alpha ? Même lecture que le pré-transfert (sb_pretransfer.alpha_client_unknown).
      const clientSearch = (result.steps || []).find((s) => s.name === "Recherche client")?.detail || "";
      if (result.client_id || clientSearch.startsWith("Client DMS")) updatePayload.alpha_client_unknown = false;
      else if (clientSearch.startsWith("Nouveau client")) updatePayload.alpha_client_unknown = true;
      if (result.dossier_id) {
        updatePayload.servicebox_dossier_id = result.dossier_id;
        setDossierId(result.dossier_id);
      }

      if (Object.keys(updatePayload).length > 0) {
        const updated = await ordresApi.update(ordre.id, updatePayload as any);
        useOrdreStore.getState().updateOrdre(updated);
        // Let a non-store-subscribed host (planning OrdreModal) refresh its local copy
        // so the N° OR field and the Actions menu reflect the transfer immediately.
        onTransferred?.(updatePayload as Partial<Ordre>);
      }

      // Persist the DMS client id on the dossier's primary contact (Carvis <-> DMS link),
      // so later edits can target/update this client instead of duplicating it.
      if (result.client_id && parentDossier?.id && parentDossier.contacts?.length) {
        const primaryId = (parentDossier.contacts.find((c) => c.is_primary) || parentDossier.contacts[0])?.id;
        if (primaryId) {
          const contacts = parentDossier.contacts.map((c) =>
            c.id === primaryId ? { ...c, dms_id: result.client_id } : c,
          );
          dossiersApi.update(parentDossier.id, { contacts } as any).catch(() => {});
        }
      }

      // The dossier goes to Alpha as soon as the RDV exists — always: the client and the
      // vehicle are in Alpha right away and the OR number comes back. Alpha decides the CT
      // printer per POSTE in its own config (nothing in the message changes that — printer
      // job log, 2026-09-23), so the message carries Carvis's poste, routed on the Alpha
      // server to a paperless printer. The garage's own transfers keep their paper CT.
      const alphaPending = !!result.dossier_id && !alphaAlreadyRan;
      if (alphaPending) {
        startAlphaTransfer({
          ordre,
          dossierId: result.dossier_id!,
          creds,
          quiet: autoRun,
          poste: alphaPosteFor(ordre),
          // A RDV that pre-dates this push may already carry its OR (imported before the
          // garage created it): read it rather than ask Alpha for a second one.
          rdvId: serviceboxRdvId(ordre) || undefined,
          onTransferred: (updates) => {
            if (updates.or_number) setOrNumber(updates.or_number);
            onTransferred?.(updates);
          },
        });
      }

      // In auto-run (client-edit push) the toast is handled by the effect below with a
      // clearer message; avoid the transfer-centric "Transfert OK" wording here.
      if (!autoRun) {
        if (result.or_number) {
          showNotification({ type: "success", message: `Transfert OK - OR n\u00B0${result.or_number}` });
        } else if (alphaPending) {
          showNotification({ type: "success", message: "RDV créé sur ServiceBox — envoi vers Alpha en cours (sans impression)" });
        } else {
          showNotification({
            type: "success",
            message: "RDV créé sur ServiceBox — transférez-le vers Alpha depuis ServiceBox, le numéro d'OR reviendra avec le devis",
          });
        }
      }

      // Don't close — let user see the steps, they can close manually
    } catch (e: any) {
      setWaitingForLock(false);
      setError(e.message || "Erreur lors du transfert");
    } finally {
      setSending(false);
    }
  };

  const hasSucceeded = steps.length > 0 && steps.every((s) => s.status !== "error");
  // Show the job while it runs or when it failed (whoever started it); a finished one only
  // if it belongs to this modal's transfer.
  const knownOrNumber = orNumber || ordre.or_number;
  // A RDV ServiceBox created before its dossier carries "rdv:<rdvId>" instead of a real
  // dossier id (see rdv_group_key): there is no panier to valorise, so no OR to create.
  const provisionalDossier = !!dossierId && dossierId.startsWith("rdv:");
  // Imported from ServiceBox and still without an OR. Re-transferring is the wrong move
  // (it asks for a duplicate RDV); the number comes from a read, once the garage has
  // transferred the dossier to Alpha from ServiceBox.
  const importedNeedsOr = !autoRun && wouldDuplicateOnServiceBox && !knownOrNumber;
  // Slot warnings only predict a refusal on CREATION. The RDV already exists on ServiceBox:
  // nothing is being booked, so they are pure noise here.
  const showSlotWarnings = !wouldDuplicateOnServiceBox;

  // ─── Auto-run (push a client edit onto an already-transferred RDV) ───────────
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (!autoRun || autoRanRef.current) return;
    // Wait until the agenda options have loaded (réceptionnaire/équipe defaulted) and the
    // ordre is actually transferable, then fire the update once.
    if (!selectedReceptionnaire || !selectedEquipe || !canTransfer) return;
    autoRanRef.current = true;
    handleTransfer();
  }, [autoRun, selectedReceptionnaire, selectedEquipe, canTransfer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-update success: toast (so there's a trace after the modal closes) + auto-close.
  useEffect(() => {
    if (autoRun && hasSucceeded) {
      showNotification({ type: "success", message: "Client mis à jour dans ServiceBox" });
      const t = setTimeout(onClose, 1400);
      return () => clearTimeout(t);
    }
  }, [autoRun, hasSucceeded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-update failure: toast so the error isn't lost when the modal is dismissed.
  useEffect(() => {
    if (autoRun && error) {
      showNotification({ type: "error", message: `Mise à jour ServiceBox échouée : ${error}` });
    }
  }, [autoRun, error]); // eslint-disable-line react-hooks/exhaustive-deps

  if (autoRun) {
    const blocked = !loading && !canTransfer && !sending && !hasSucceeded;
    return (
      <BaseModal onClose={onClose} title="Mise à jour ServiceBox" startIcon={<Icon1 name="sett-servicebox" size={20} />} width="max-w-sm">
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
          {error || blocked ? (
            <>
              <div className="text-h3 text-red-600">{error || "Informations manquantes pour mettre à jour ServiceBox."}</div>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-neutral-100 text-h3 font-medium">Fermer</button>
            </>
          ) : hasSucceeded ? (
            <>
              <svg width={28} height={28} viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <div className="text-h3 text-neutral-700">Client mis à jour dans ServiceBox</div>
            </>
          ) : (
            <>
              <div className="w-7 h-7 rounded-full border-[3px] border-neutral-200 border-t-neutral-700 animate-spin" />
              <div className="text-h3 text-neutral-500">{waitingForLock ? PROXY_BUSY_MESSAGE : "Mise à jour du client dans ServiceBox…"}</div>
            </>
          )}
        </div>
      </BaseModal>
    );
  }

  return (
    <BaseModal
      onClose={onClose}
      title="Transfert ServiceBox"
      startIcon={<Icon1 name="sett-servicebox" size={20} />}
      width="max-w-xl"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Non-Chrome warning — the local proxy transfer is only reliable on Chrome/Chromium */}
        {!isChromiumBrowser() && (
          <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-h3 flex flex-row items-start gap-2">
            <Icon1 name="warning" size={16} color="#EA580C" />
            <div>
              <div className="font-medium mb-0.5">Navigateur non recommandé</div>
              <div>
                Pour éviter des bugs lors du transfert vers ServiceBox, utilisez <span className="font-medium">Google Chrome</span>.
                Le transfert via le proxy local n'est fiable que sur Chrome.
              </div>
            </div>
          </div>
        )}

        {/* Imported from ServiceBox, no OR yet — the RDV exists, only Alpha's OR is missing.
            Re-creating the RDV is the wrong move, creating the OR alone is the right one. */}
        {importedNeedsOr && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-h3 flex flex-col gap-2">
            <div className="font-medium">Ce RDV vient de ServiceBox — il n'a pas encore de numéro d'OR</div>
            <div>
              Il a été créé côté ServiceBox puis importé dans Carvis (RDV n°{ordre.sb_agenda_rdv_id}). Pas besoin
              de le re-transférer : son dossier est déjà là-bas. Il ne manque que l'OR, créé par Alpha à partir de
              ce dossier.
            </div>
            {provisionalDossier ? (
              <div className="text-h4 opacity-80">
                ServiceBox n'a pas encore ouvert de dossier pour ce RDV : il n'y a rien à transférer vers
                Alpha. Faites « Synchro. Dossier » sur le RDV dans ServiceBox, puis revenez ici.
              </div>
            ) : (
              <>
                {/* Carvis ne crée jamais l'OR : Alpha imprime la CT chiffrée dès qu'il en
                    crée un. Le transfert reste le geste du garage, depuis ServiceBox. */}
                <div className="text-h4 opacity-80">
                  Transférez le dossier vers Alpha depuis ServiceBox — ce transfert reste votre geste, parce
                  qu'Alpha imprime la CT chiffrée dès qu'il crée l'OR. Pensez à reporter d'abord vos
                  interventions Carvis : une ligne ajoutée après la création de l'OR ne l'atteint plus.
                </div>
                <div>
                  <button
                    onClick={fetchOrNumber}
                    disabled={fetchingOr}
                    className="px-3 py-1.5 rounded-lg text-h4 font-medium bg-white border border-amber-300 hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    {fetchingOr ? "Lecture sur ServiceBox…" : "Récupérer le numéro d'OR"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Already on ServiceBox WITH its OR — a transfer here would ask for a DUPLICATE RDV */}
        {wouldDuplicateOnServiceBox && !importedNeedsOr && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-h3">
            <div className="font-medium mb-1">Ce rendez-vous est déjà sur ServiceBox</div>
            <div>
              Il y a été créé côté ServiceBox puis importé dans Carvis (RDV n°{ordre.sb_agenda_rdv_id}
              {knownOrNumber ? `, OR n°${knownOrNumber}` : ""}). Le transférer demanderait à ServiceBox
              d'en créer un <span className="font-medium">second</span> : refusé (« CCS indisponible ») tant que
              le créneau est le sien, dupliqué dès qu'il a bougé dans Carvis.
            </div>
            <div className="mt-1 text-h4 opacity-80">
              Pour reporter une intervention ajoutée dans Carvis, utilisez « Pousser les interventions vers
              ServiceBox » dans le menu Actions.
            </div>
          </div>
        )}

        {/* Missing-info gate — block transfer until essential data is present */}
        {(missingFields.length > 0 || datesOutOfOrder) && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-h3">
            <div className="font-medium mb-1">Informations manquantes avant transfert</div>
            {missingFields.length > 0 && (
              <ul className="list-disc pl-5">
                {missingFields.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
            {datesOutOfOrder && (
              <div className="mt-1">La date de réception doit être avant la date de restitution.</div>
            )}
            <div className="mt-1 text-h4 opacity-80">Complétez ces champs dans l'ordre / le dossier avant de transférer vers ServiceBox.</div>
          </div>
        )}

        {/* Off-grid time warning — usual cause of "CCS indisponible" */}
        {showSlotWarnings && offGridTimes.length > 0 && (
          <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-h3">
            <div className="font-medium mb-1">Horaire hors créneau ServiceBox</div>
            <div>
              {offGridTimes.join(" et ")} {offGridTimes.length > 1 ? "ne sont pas alignées" : "n'est pas alignée"} sur un créneau de 7,5 min.
              ServiceBox risque de refuser le transfert (« CCS indisponible »). Ajustez l'horaire sur un créneau valide dans l'ordre.
            </div>
          </div>
        )}

        {/* Reception slot-collision warning — ServiceBox refuses an already-taken column */}
        {(showSlotWarnings ? [receptionConflict?.reception, receptionConflict?.restitution] : [])
          .filter((e): e is ReceptionConflictEvent => !!e && e.conflict)
          .map((ev) => (
            <div key={ev.type} className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-h3 flex flex-col gap-2">
              <div>
                <span className="font-medium">Créneau déjà pris</span> — la {ev.type === "reception" ? "réception" : "restitution"} ({ev.conflict_with?.time}) est occupée en {ev.category_label}
                {ev.conflict_with?.client_name ? ` par ${ev.conflict_with.client_name}` : ""}
                {ev.conflict_with?.plate ? ` (${ev.conflict_with.plate})` : ""}. ServiceBox refusera ce créneau.
              </div>
              {ev.free_receptions.length > 0 ? (
                <div className="flex flex-row flex-wrap gap-2">
                  {ev.free_receptions.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => switchReception(ev, r.id)}
                      className="px-3 py-1.5 rounded-lg text-h4 font-medium bg-white border border-amber-300 hover:opacity-80 transition-opacity"
                    >
                      Basculer sur {r.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-h4 opacity-80">Aucune autre réception libre sur ce créneau.</div>
              )}
            </div>
          ))}

        {/* Summary */}
        <div className="flex flex-col gap-2 p-3 rounded-lg bg-neutral-50 border border-neutral-200">
          <div className="text-h2 font-semibold text-neutral-700">
            {wouldDuplicateOnServiceBox ? "Données de l'ordre" : "Données à transférer"}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-h3">
            <span className="text-neutral-400">Type de dossier</span>
            <span className={`text-neutral-700 ${!dossierType ? "text-red-500 font-medium" : ""}`}>
              {dossierType ? DOSSIER_TYPE_LABELS[dossierType] || dossierType : "Non défini"}
            </span>
            <span className="text-neutral-400">Client</span>
            <span className="text-neutral-700">{primaryContact?.prenom || ""} {primaryContact?.nom || ordre.name}</span>
            <span className="text-neutral-400">Telephone</span>
            <span className="text-neutral-700">{primaryContact?.phone || "\u2014"}</span>
            <span className="text-neutral-400">Vehicule</span>
            <span className="text-neutral-700">{vehicle?.marque || ""} {vehicle?.modele || ordre.modele || ""}</span>
            <span className="text-neutral-400">Immatriculation</span>
            <span className="text-neutral-700">{ordre.immatriculation || vehicle?.plate || "\u2014"}</span>
            <span className="text-neutral-400">VIN</span>
            <span className="text-neutral-700">{vehicle?.vin || "\u2014"}</span>
            <span className="text-neutral-400">Interventions</span>
            <span className="text-neutral-700">
              {ordre.interventions.length > 0
                ? ordre.interventions.map((i) => i.label).join(", ")
                : "\u2014"}
            </span>
          </div>
        </div>

        {/* Options */}
        {loading ? (
          <div className="flex items-center justify-center py-6 text-neutral-400 text-h3">
            Chargement des options ServiceBox...
          </div>
        ) : !hasSucceeded && !wouldDuplicateOnServiceBox ? (
          <div className="flex flex-col gap-3">
            {sites.length > 0 && (
              <DropDown
                label="Point de vente (marque)"
                value={selectedPdv}
                options={sites.map((s) => ({ value: s.pdv_id, label: s.label }))}
                onSelect={(v) => { userTouchedPdvRef.current = true; setSelectedPdv(v as string); }}
              />
            )}
            <DropDown
              label="Réceptionnaire (réception)"
              value={selectedReceptionnaire}
              options={receptionnaires.map((r) => ({ value: r.id, label: receptionnaireLabel(r) }))}
              onSelect={(v) => { userTouchedRecepRef.current = true; setSelectedReceptionnaire(v as string); }}
            />
            <DropDown
              label="Réceptionnaire (restitution)"
              value={selectedRestitutionReceptionnaire}
              options={receptionnaires.map((r) => ({ value: r.id, label: receptionnaireLabel(r) }))}
              onSelect={(v) => { userTouchedRecepRef.current = true; setSelectedRestitutionReceptionnaire(v as string); }}
            />
            {receptionnaireHint && (
              <span className={`text-h4 ${recIsLinked ? "text-neutral-400" : "text-amber-600"}`}>
                {receptionnaireHint}
              </span>
            )}
            <DropDown
              label="Équipe"
              value={selectedEquipe}
              options={equipes.map((e) => ({ value: e.id, label: e.name }))}
              onSelect={(v) => setPickedEquipe(v as string)}
            />
          </div>
        ) : null}

        {/* RDV on ServiceBox without an OR: the garage has not transferred the dossier to
            Alpha yet. Carvis never does that — it reads the number back. */}
        {!autoRun && !wouldDuplicateOnServiceBox && !knownOrNumber && !!dossierId && !provisionalDossier && !sending && !loading && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-h3 flex flex-col gap-2">
            <div>
              <span className="font-medium">RDV sur ServiceBox, sans OR</span> — le dossier n'a pas encore été
              transféré vers Alpha. Faites-le depuis ServiceBox, puis récupérez le numéro ici.
            </div>
            <div>
              <button
                onClick={fetchOrNumber}
                disabled={fetchingOr}
                className="px-3 py-1.5 rounded-lg text-h4 font-medium bg-white border border-amber-300 hover:opacity-80 transition-opacity disabled:opacity-50"
              >
                {fetchingOr ? "Lecture sur ServiceBox…" : "Récupérer le numéro d'OR"}
              </button>
            </div>
          </div>
        )}

        {/* Step-by-step progress */}
        {steps.length > 0 && (
          <div className="flex flex-col gap-0 p-3 rounded-lg border border-neutral-200 bg-white">
            <div className="text-h2 font-semibold text-neutral-700 mb-1">Progression</div>
            {steps.map((step, i) => (
              <StepLine key={i} step={step} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-h3">
            {error}
          </div>
        )}

        {/* Connection test result */}
        {connectionTest && (
          <div
            className="p-3 rounded-lg border text-h3"
            style={{
              background: connectionTest.connected ? "#F0FDF4" : "#FEF2F2",
              borderColor: connectionTest.connected ? "#BBF7D0" : "#FECACA",
              color: connectionTest.connected ? "#166534" : "#991B1B",
            }}
          >
            <div className="font-medium mb-1">
              {connectionTest.connected ? "Connexion OK" : "Echec de connexion"}
            </div>
            <div className="text-h4">
              VPN/Reseau: {connectionTest.servicebox_reachable ? "OK" : "Echec"} |
              Session: {connectionTest.session_ok ? "OK" : "Echec"}
            </div>
            {connectionTest.detail && (
              <div className="text-h4 mt-1 opacity-80">{connectionTest.detail}</div>
            )}
          </div>
        )}

        {/* Client search result */}
        {clientSearchResult && (
          <div
            className="p-3 rounded-lg border text-h3"
            style={{
              background: clientSearchResult.found ? "#F0FDF4" : "#FEF2F2",
              borderColor: clientSearchResult.found ? "#BBF7D0" : "#FECACA",
              color: clientSearchResult.found ? "#166534" : "#991B1B",
            }}
          >
            <div className="font-medium mb-1">
              {clientSearchResult.found ? "Client trouve dans le DMS" : "Client non trouve"}
            </div>
            <div className="text-h4">{clientSearchResult.detail}</div>
          </div>
        )}

        {/* Logs panel */}
        {showLogs && (
          <div className="flex flex-col border border-neutral-200 rounded-lg overflow-hidden">
            <div className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-200">
              <span className="text-h3 font-medium text-neutral-600">Logs du proxy</span>
              <div className="flex flex-row items-center gap-2">
                <button
                  onClick={() => {
                    const text = logs.map((l) => `${l.ts.split("T")[1]?.slice(0, 8)} [${l.step}] ${l.message}`).join("\n");
                    navigator.clipboard.writeText(text);
                    showNotification({ type: "success", message: "Logs copies" });
                  }}
                  className="text-h4 text-blue-500 hover:text-blue-700"
                >
                  Copier
                </button>
                <button onClick={() => setShowLogs(false)} className="text-h4 text-neutral-400 hover:text-neutral-600">
                  Fermer
                </button>
              </div>
            </div>
            <div className="max-h-[200px] overflow-y-auto p-2 bg-neutral-900 font-mono text-[11px] leading-[16px]">
              {logs.length === 0 ? (
                <div className="text-neutral-500">Aucun log</div>
              ) : (
                logs.map((log, i) => {
                  const color = log.level === "error" ? "#F87171" : log.level === "warn" ? "#FBBF24" : "#9CA3AF";
                  return (
                    <div key={i} style={{ color }}>
                      <span className="text-neutral-600">{log.ts.split("T")[1]?.slice(0, 8)}</span>
                      {" "}
                      {log.step && <span className="text-blue-400">[{log.step}]</span>}
                      {" "}{log.message}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Debug results */}
        {debugResults && (
          <div className="flex flex-col border border-neutral-200 rounded-lg overflow-hidden">
            <div className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-200">
              <span className="text-h3 font-medium text-neutral-600">Debug Auth</span>
              <button onClick={() => setDebugResults(null)} className="text-h4 text-neutral-400 hover:text-neutral-600">
                Fermer
              </button>
            </div>
            <div className="max-h-[300px] overflow-y-auto p-2 bg-neutral-900 font-mono text-[11px] leading-[16px]">
              {debugResults.map((r, i) => {
                const color = r.result === "ok" || r.result.includes("200") ? "#34D399" : r.result === "error" || r.result.includes("401") ? "#F87171" : "#9CA3AF";
                return (
                  <div key={i} className="py-0.5">
                    <span style={{ color }} className="font-bold">[{r.result}]</span>
                    {" "}
                    <span className="text-blue-300">{r.test}</span>
                    {" "}
                    <span className="text-neutral-400 break-all">{r.detail}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Diagnostic links */}
        <div className="flex flex-row items-center gap-3 text-[11px]">
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer disabled:opacity-50"
          >
            {testing ? "Test..." : "Tester connexion"}
          </button>
          <button
            onClick={handleSearchClient}
            disabled={searchingClient}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer disabled:opacity-50"
          >
            {searchingClient ? "Recherche..." : "Chercher client DMS"}
          </button>
          <button
            onClick={handleDebugAuth}
            disabled={debugging}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer disabled:opacity-50"
          >
            {debugging ? "Debug..." : "Debug auth"}
          </button>
          <button
            onClick={handleFetchLogs}
            className="text-neutral-400 hover:text-neutral-600 cursor-pointer"
          >
            Logs
          </button>
        </div>

        {/* Actions */}
        <div className="flex flex-row items-center justify-end gap-2 pt-1">
          {hasSucceeded ? (
            <>
              {dossierId && (
                <SecondaryButton
                  label="Voir le Dossier ServiceBox"
                  startIcon={<Icon1 name="sett-servicebox" size={16} />}
                  onClick={() =>
                    window.open(
                      `https://servicebox.mpsa.com/panier/panierSetCurrent.do?id=${dossierId}`,
                      "_blank",
                    )
                  }
                />
              )}
              <Button label="Fermer" onClick={onClose} />
            </>
          ) : (
            <>
              {kmEmpty && (
                <div className="flex items-center gap-2 mr-auto px-3 py-2 rounded-lg" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}>
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M10 2L1 18h18L10 2z" fill="#EA580C" />
                    <text x="10" y="16" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#fff">!</text>
                  </svg>
                  <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#C2410C" }}>Kilométrage du véhicule vide</span>
                </div>
              )}
              <SecondaryButton label={wouldDuplicateOnServiceBox ? "Fermer" : "Annuler"} onClick={onClose} />
              {wouldDuplicateOnServiceBox && !importedNeedsOr ? null : importedNeedsOr ? (
                <Button
                  label={fetchingOr ? "Lecture sur ServiceBox..." : "Récupérer le numéro d'OR"}
                  onClick={fetchOrNumber}
                  disabled={fetchingOr || provisionalDossier}
                  loadingBar={fetchingOr}
                />
              ) : (
                <Button
                  label={waitingForLock ? "Un transfert est en cours, le tien démarre juste après…" : sending ? "Transfert en cours..." : "Transferer"}
                  onClick={handleTransfer}
                  disabled={loading || sending || !selectedReceptionnaire || !selectedEquipe || !canTransfer}
                  loadingBar={sending}
                />
              )}
            </>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
